import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { STEPS } from "@/components/walkthrough/data";
import { CallStep, MissedStep, ConsentStep, SmsStep } from "@/components/walkthrough/PhoneSteps";
import { LeadStep, AlertStep, CalendarStep, DoneStep } from "@/components/walkthrough/DashboardSteps";

export default function Walkthrough() {
  const [i, setI] = useState(0);
  const step = STEPS[i];
  const last = i === STEPS.length - 1;
  const next = () => setI((x) => Math.min(x + 1, STEPS.length - 1));
  const back = () => setI((x) => Math.max(x - 1, 0));
  const restart = () => setI(0);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "ArrowRight") next(); if (e.key === "ArrowLeft") back(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const body = {
    call: <CallStep />, missed: <MissedStep />, consent: <ConsentStep />, sms: <SmsStep />, lead: <LeadStep />, alert: <AlertStep />,
    calendar: <CalendarStep />, done: <DoneStep />,
  }[step.key];

  return (
    <div className="p-6 lg:p-10 max-w-6xl" data-testid="walkthrough-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <span className="inline-block px-3 py-1 rounded-full border border-amber-400/40 bg-amber-400/10 text-amber-200 text-[11px] font-mono uppercase tracking-widest" data-testid="walkthrough-label">
            Interactive Product Demonstration — Simulated Data
          </span>
          <h1 className="font-display text-4xl sm:text-5xl font-bold mt-4">From missed call to booked estimate</h1>
        </div>
        <Button variant="outline" onClick={restart} data-testid="walkthrough-restart" className="border-slate-700 rounded-full"><RotateCcw className="w-4 h-4 mr-2" /> Restart Demo</Button>
      </div>

      <div className="flex gap-1.5 mt-8" data-testid="walkthrough-progress">
        {STEPS.map((s, n) => (
          <button key={s.key} onClick={() => setI(n)} title={s.title} data-testid={`walkthrough-dot-${n + 1}`}
            className={`h-1.5 flex-1 rounded-full transition-colors ${n <= i ? "bg-orange-500" : "bg-slate-800 hover:bg-slate-700"}`} />
        ))}
      </div>
      <div className="mt-5">
        <p className="font-mono text-xs uppercase tracking-widest text-orange-400" data-testid="walkthrough-step-count">Step {i + 1} of {STEPS.length}</p>
        <h2 className="font-display text-2xl font-bold text-white mt-1" data-testid="walkthrough-step-title">{step.title}</h2>
        <p className="text-slate-400 mt-1">{step.say}</p>
      </div>

      <div className="min-h-[520px] mt-6 flex items-center">
        <AnimatePresence mode="wait">
          <motion.div key={step.key} className="w-full" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.25 }}>
            {body}
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-800">
        <Button variant="ghost" onClick={back} disabled={i === 0} data-testid="walkthrough-back" className="text-slate-300"><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
        <p className="text-xs text-slate-500 text-center max-w-md hidden sm:block">Booking by text is a simulated preview of a planned feature and is not currently active. Today, estimates are scheduled by the roofing team in the dashboard.</p>
        {last ? (
          <Button onClick={restart} data-testid="walkthrough-finish" className="bg-orange-500 hover:bg-orange-600 text-slate-900 rounded-full px-6"><RotateCcw className="w-4 h-4 mr-2" /> Restart Demo</Button>
        ) : (
          <Button onClick={next} data-testid="walkthrough-next" className="bg-orange-500 hover:bg-orange-600 text-slate-900 rounded-full px-6">Next <ChevronRight className="w-4 h-4 ml-1" /></Button>
        )}
      </div>
    </div>
  );
}
